// src/oura/components/newscan/steps/ReviewStep.tsx
//
// Step 3, "Review the 50". While the draft is being written: DraftBuilding.
// Once it is ready, decisions first:
//   - the head: "<n> decisions before you can start. The rest passed every
//     check.", with Questions | Words tabs on the right;
//   - HealthLine: the shape, every outlet asked, the profession word against
//     its cap, Search Console;
//   - DecisionList: the cards to settle (a recipe change first), the outcome
//     line with Undo, Search Console suggestions, notes;
//   - the 50 by group (QuestionList, rows edit in place), or the Words view.
//     The health line's "<n> match real searches" chip filters the 50 to the
//     questions with a search, most searched first.
// Foot: Back · "<k> decisions left" · Approve the 50, which goes on to Start.
//
// A decision's client-side options move the screen here: "Edit 44 myself"
// opens that row's editor (unfolding its group), "Type one" opens the Words
// view at that word, "Connect" starts Search Console, "Choose each" opens the
// recipe card's list and hands the foot to it.
//
// First AI check (JOURNEY-SPEC v1.1 §2.5): the pitch line sits directly under
// the head (the server's `presale_set` sentence, never a note), with "See the
// changes" when the 50 differ from the pitch (it shows only those rows). The
// form and quote cards come in the decision list; "Name the area", "Add an
// outlet" and "Choose the 9" open the Business step. "For the plan" follows
// the question decisions (PlanDecisions). No trend break: no trend yet. An
// approval that carried from the pitch says whose it was.
//
// Brand mode (v4.5, SPEC-v45 §7): the 50 are grouped as "Recommend you ·
// problems", "Recommend you · products", "Cite your site" and "Business
// buyers", each row with its market chip and its Google search; the tabs are
// Questions | Searches (the Google searches pulled, in place of Words), and a
// word link lands in the Searches view's last fold.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DecisionType, ScanDraft, ScanDraftQuestion, ScanDraftWord } from "@service/aeo/scanDraftTypes";
import { CheckIcon, RefreshCwIcon } from "lucide-react";
import { getOnboarding, type FormAnswers as OnboardingAnswers } from "@service/aeo/onboarding";
import { getAssessment, type AeoQuery, type AeoQuestionCorrected } from "@service/aeo";
import type { QuestionEdit } from "@oura/components/newscan/QuestionRow";
import { cn } from "@oura/lib/cn";
import {
    DrawerNotice,
    DrawerPulse,
    FOCUS_RING,
    FoldScope,
    PillButton,
    Seg,
    Surface,
    TYPE,
    useFoldOpener,
    useMediaQuery,
    REDUCED_MOTION,
} from "@oura/ui";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { Refusal } from "@oura/components/onboarding/fields";
import { FRAME, plural } from "@oura/newscan/copy";
import { StepHead } from "@oura/components/newscan/StepHead";
import { DraftBuilding } from "@oura/components/newscan/DraftBuilding";
import { HealthLine } from "@oura/components/newscan/HealthLine";
import { DecisionList } from "@oura/components/newscan/DecisionList";
import { openDecisions, openGates } from "@oura/components/newscan/openDecisions";
import { healthHasUnmet } from "@oura/components/newscan/healthCheck";
import { QuestionList, type DisplayGroup } from "@oura/components/newscan/QuestionList";
import { setOutletIntent } from "@oura/newscan/outletIntent";
import { townForArea } from "@oura/newscan/towns";
import WordsView from "@oura/components/newscan/WordsView";
import SearchesView from "@oura/components/newscan/SearchesView";
import { BRAND_GROUPS, brandGroupOf, isBrandSet, marketName, marketOf } from "@oura/components/newscan/brandReview";
import RecipeChangeCard, { type RecipeChooseFoot } from "@oura/components/newscan/RecipeChangeCard";
import { PlanDecisions } from "@oura/components/newscan/PlanDecisions";
import { carriedText, isFirstCheck, isPlanCard, pitchLine } from "@oura/components/newscan/firstCheckFacts";
import { CustomerQuestions, FormContext, GoalCard } from "@oura/components/newscan/FormAnswers";
import { showsCoreService } from "@oura/newscan/coreChoice";
import { SectionHead } from "@oura/components/newscan/SectionHead";
import { BranchesCard, useApplyBranches } from "@oura/components/newscan/BranchesCard";
import { TellOura, TellOuraDock } from "@oura/components/newscan/TellOura";
import { BRANCHES_CARD_ON } from "@oura/newscan/flags";
import { branchesTitle, isBranchCard, openBranchCards, placeNameGroups, showsBranches } from "@oura/newscan/branches";
import { PlaceNameCard } from "@oura/components/newscan/PlaceNameCard";
import { ReaskConfirm, ReaskProgress } from "@oura/components/newscan/Reask";
import { reaskQuestions, useReask } from "@oura/newscan/reask";

const SCOPE = "newscan-review";
const PANEL_ID = "review-panel";

type View = "questions" | "words" | "searches";

/**
 * The right panel's filter on the two-panel Review (First AI check): every
 * row, the rows with an open decision by kind, staff's own, the locked ones,
 * or the rows one card is about ("Show the 6").
 */
type RowFilter = { kind: "all" | "blocker" | "decide" | "edited" | "locked" | "changed" } | { kind: "card"; key: string; label: string; ns: number[] };
const ALL_ROWS: RowFilter = { kind: "all" };

/**
 * The two-panel Review's right side: one titled section per kind of decision, in
 * place of one "To decide" pile (Jovi, 20 Oct). The core service, the branches and
 * "the business name as a place" have their own cards; every other open card lands
 * in the section its type names, and a type not listed here in "Other checks", so
 * a new backend type is never dropped. Notes (info) close the list, untitled.
 */
const DECISION_SECTIONS: { id: string; title: string; types: DecisionType[] }[] = [
    { id: "pitch", title: "The pitch scan", types: ["pitch_running"] },
    { id: "services", title: "Services", types: ["form_service_new", "form_service_missing", "service_unused"] },
    {
        id: "wording",
        title: "Questions to fix",
        types: [
            "lint_error",
            "lint_error_set",
            "duplicate",
            "missing_word",
            "opposite_meaning",
            "differentiator_too_long",
            "category_in_problem_row",
            "claim_in_question",
            "place_in_brand_row",
        ],
    },
    {
        id: "places",
        title: "Places",
        types: ["outlet_unverified", "outlet_undeclared", "form_outlet_new", "form_outlet_missing", "outlets_vs_plan", "country_only_place", "outlets_exceed_slots", "towns_thin"],
    },
    {
        id: "shape",
        title: "How the 50 are built",
        types: ["mode_mismatch", "recipe_change", "set_narrower", "category_cap", "scope_warning", "language_rows", "research_unavailable", "trend_break"],
    },
];
/** Never in a section: their own cards, the plan, the pitch line, Search Console. */
const OWN_PLACE: DecisionType[] = ["form_goal_core", "plan_keywords", "plan_outlet_names", "presale_set", "gsc_candidate"];

const COPY = {
    title: (total: number) => `Review the ${total}`,
    toDecide: "To decide",
    toDecideSub: "Answer each card below, or tell Oura in your own words.",
    toDecideSubDocked: "Answer each card below, or press Tell Oura at the bottom right and say it in your own words.",
    theQuestions: (total: number) => `The ${total} questions`,
    needs: (n: number) => `${plural(n, "decision")} before you can start.`,
    rest: "The rest passed every check.",
    clean: "Every question passed every check. Read through, change anything you like, then start.",
    // Nothing gates the start, but the health line or the notes still say something.
    cleanWithNotes: (n: number) => `Nothing left to decide. ${plural(n, "note")} below.`,
    cleanNoNotes: "Nothing left to decide. Read through, change anything you like, then start.",
    tabs: "Review",
    questions: "Questions",
    words: "Words",
    searches: "Searches",
    left: (n: number) => `${plural(n, "decision")} left`,
    stale: "Draft again first",
    staleNotice: "The business details changed after this draft. Draft the 50 again to use them.",
    draftAgain: "Draft again",
    approve: (total: number) => `Approve the ${total}`,
    reask: (n: number) => (n === 1 ? "Re-ask 1 question" : `Re-ask ${n} questions`),
    approved: "Approved. Reopen it to change anything.",
    started: "This set started a scan.",
    reopen: "Reopen",
    ready: (total: number, left: number, allClear: boolean) =>
        left > 0
            ? `Review the ${total}. ${plural(left, "decision")} before you can start.`
            : allClear
              ? `Review the ${total}. Every question passed every check.`
              : `Review the ${total}. Nothing left to decide.`,
    toStart: "Continue to start",
    flagship: "flagship",
    market: (m: string) => `Market · ${m}`,
    specialty: "Specialty",
    intent: "Intent",
    bySearch: (n: number) => `Showing ${plural(n, "question")} by searches`,
    showingAll: (total: number) => `Showing all ${total}`,
    seeChanges: "See the changes",
    changesOnly: (n: number) => `Showing the ${plural(n, "changed question")}.`,
    showAll: (total: number) => `Show all ${total}`,
    carried: (who: string) => `${who}. Reopen it to change anything.`,
    filters: "Show",
    filterAll: (n: number) => `All ${n}`,
    filterBlocker: "Must fix",
    filterDecide: "To decide",
    filterEdited: "Edited",
    filterLocked: "Locked",
    filterChanged: "Changed",
    filterBlockerTip: "In a card that blocks Approve: it names the business, repeats another question, or misses a word or a place.",
    filterDecideTip: "In a card that is still open. Answer the card on the left.",
    forCard: (n: number, label: string) => `Showing the ${plural(n, "question")} for “${label}”.`,
    fromClient: "From the client",
    formFailed: "We couldn't load the client's form answers. Try again before reviewing the 50.",
    formRetry: "Try again",
    coreService: "Core service",
    nameAsPlace: "The business name used as a place",
    otherChecks: "Other checks",
    customerQs: "Optional: what customers ask",
    customerQsSub: "Questions from the client's form that none of the 50 ask. Swap one in only if it matters.",
    sectionDone: "Done",
} as const;

const slug = (s: string) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

/** Brand mode: the four groups in order, each with its one intro line. Business buyers is absent for a shopper-only store. */
const BRAND_TONE: Record<string, DisplayGroup["tone"]> = { pick_problem: "teal", pick_product: "indigo", source: "fuchsia", supplier: "blue" };

function brandGroups(draft: ScanDraft): DisplayGroup[] {
    const rows = [...draft.questions].sort((x, y) => x.n - y.n);
    return BRAND_GROUPS.map((g) => ({
        id: g.id,
        title: g.title,
        intro: g.intro,
        tone: BRAND_TONE[g.id],
        rows: rows.filter((q) => brandGroupOf(q) === g.id),
    })).filter((g) => g.rows.length > 0);
}

/** One group per outlet (or the market, or a showroom) for the location rows, then Specialty and Intent. */
function displayGroups(draft: ScanDraft): DisplayGroup[] {
    if (isBrandSet(draft)) return brandGroups(draft);
    const b = draft.business;
    const market = (b.market ?? "").trim();
    // Both is Local v4.5 (its showrooms are outlets): only an older Online draft has market rows.
    const sellsOnline = b.buy === "online";
    const flagship = b.outlets[0]?.name.trim().toLowerCase() ?? "";
    const rows = [...draft.questions].sort((x, y) => x.n - y.n);
    const out: DisplayGroup[] = [];
    const byId = new Map<string, DisplayGroup>();

    rows.filter((q) => q.check === "location").forEach((q) => {
        const place = (q.fill || market || b.city || "").trim();
        const isMarket = sellsOnline && market !== "" && place.toLowerCase() === market.toLowerCase();
        const id = isMarket ? "loc:market" : `loc:${slug(place) || "place"}`;
        let g = byId.get(id);
        if (!g) {
            const title = isMarket
                ? COPY.market(market)
                : b.outlets.length > 1 && place.toLowerCase() === flagship
                    ? `${place} · ${COPY.flagship}`
                    : place;
            g = { id, title, tone: "teal", rows: [] };
            byId.set(id, g);
            out.push(g);
        }
        g.rows.push(q);
    });
    const specialty = rows.filter((q) => q.check === "specialty");
    const intent = rows.filter((q) => q.check === "intent");
    if (specialty.length) out.push({ id: "specialty", title: COPY.specialty, tone: "fuchsia", rows: specialty });
    if (intent.length) out.push({ id: "intent", title: COPY.intent, tone: "indigo", rows: intent });
    return out;
}

/** Keyed by the draft: its filters, recipe chooser and focused word belong to one draft and never carry to the next. */
export default function ReviewStep() {
    const { ctl } = useScanFlow();
    return (
        <FoldScope value={SCOPE}>
            <Review key={ctl.draft?.id ?? "loading"} />
        </FoldScope>
    );
}

function Review() {
    const flow = useScanFlow();
    const { ctl, foot, goTo, canReach } = flow;
    const draft = ctl.draft;
    const reduced = useMediaQuery(REDUCED_MOTION);
    const openGroup = useFoldOpener(SCOPE);

    const [viewPicked, setView] = useState<View>("questions");
    const brand = draft ? isBrandSet(draft) : false;
    // Brand mode has Searches where local has Words: a pick from the other mode reads as its twin.
    const view: View = brand && viewPicked === "words" ? "searches" : !brand && viewPicked === "searches" ? "words" : viewPicked;
    const [openN, setOpenN] = useState<number | null>(null);
    const [focusWord, setFocusWord] = useState<string | null>(null);
    const [chooseFoot, setChooseFoot] = useState<RecipeChooseFoot | null>(null);
    const [searchOnly, setSearchOnly] = useState(false);
    // First AI check: "See the changes" shows only the rows that differ from the pitch.
    const [changesOnly, setChangesOnly] = useState(false);
    const [rowFilter, setRowFilter] = useState<RowFilter>(ALL_ROWS);
    const seenSections = useRef(new Set<string>());
    // "From the client": the draft does not carry the form yet, so read the filed answers off the onboarding.
    const [formAnswers, setFormAnswers] = useState<OnboardingAnswers | null>(null);
    // A failed read is said, with Try again: read as "no answers" it let staff review without the
    // client's goals and never know (Jack, #4451). A 404 is a real "no onboarding" (getOnboarding: null).
    const [formLoadFailed, setFormLoadFailed] = useState(false);
    const [formLoadAttempt, setFormLoadAttempt] = useState(0);
    // The business whose form has been read (null: none yet). Read against formFrom, so the render
    // before the effect resets it already counts as not loaded.
    const [formReadFor, setFormReadFor] = useState<string | null>(null);
    const formFrom = isFirstCheck(draft) && !draft?.firstCheck?.form ? flow.businessId : null;
    useEffect(() => {
        setFormAnswers(null);
        setFormLoadFailed(false);
        setFormReadFor(null);
        if (!formFrom) return;
        let live = true;
        getOnboarding(formFrom)
            .then((o) => {
                if (!live) return;
                setFormAnswers(o?.form.answers ?? null);
                setFormReadFor(formFrom);
            })
            .catch(() => {
                if (live) setFormLoadFailed(true);
            });
        return () => {
            live = false;
        };
    }, [formFrom, formLoadAttempt]);
    // Nothing is approved or re-asked without the client's form in view: not while it loads, not after
    // a failed read (Jack, #4501).
    const formUnavailable = Boolean(formFrom) && formReadFor !== formFrom;

    const first = isFirstCheck(draft);
    const changedNs = useMemo(() => (first && draft?.diff ? draft.diff.changed : []), [first, draft]);
    const showingChanges = changesOnly && changedNs.length > 0;
    const allGroups = useMemo(() => (draft ? displayGroups(draft) : []), [draft]);
    const groups = useMemo(() => {
        if (!showingChanges) return allGroups;
        const keep = new Set(changedNs);
        return allGroups.map((g) => ({ ...g, rows: g.rows.filter((q) => keep.has(q.n)) })).filter((g) => g.rows.length > 0);
    }, [allGroups, showingChanges, changedNs]);
    const words = useMemo(() => {
        const map: Record<string, ScanDraftWord> = {};
        draft?.words.forEach((w) => {
            map[w.variable] = w;
        });
        return map;
    }, [draft]);
    const gates = useMemo(() => (draft ? openGates(draft) : []), [draft]);
    // First AI check: the outlet cards fold into one "Which branches?" list, counted as one decision.
    const branches = BRANCHES_CARD_ON && showsBranches(draft);
    // The questions that use a word of the business name as a branch: one card, not one per question.
    const placeGroups = useMemo(() => (branches && draft ? placeNameGroups(draft) : []), [branches, draft]);
    // An edit that only reworded a few questions re-asks those, not the whole 50 (Reask.tsx).
    const reaskQs = useMemo(() => (draft ? reaskQuestions(draft) : null), [draft]);
    const listDraft = useMemo(() => {
        // A re-ask corrects the same scan, so no trend restarts: that card is for a full rescan.
        if (reaskQs && draft) return { ...draft, decisions: draft.decisions.filter((d) => d.type !== "trend_break") };
        if (!branches || !draft) return draft;
        const grouped = new Set(placeGroups.flatMap((g) => [...g.cards, ...g.folded].map((d) => d.id)));
        return { ...draft, decisions: draft.decisions.filter((d) => !isBranchCard(d) && !grouped.has(d.id)) };
    }, [branches, draft, placeGroups, reaskQs]);
    const left = useMemo(
        () =>
            branches && listDraft && draft
                ? openGates(listDraft).length + (openBranchCards(draft).length > 0 ? 1 : 0) + placeGroups.length
                : reaskQs && listDraft
                  ? openGates(listDraft).length
                  : gates.length,
        [branches, listDraft, draft, gates, placeGroups, reaskQs],
    );
    const applier = useApplyBranches(branches ? draft : null);
    // "Every check passed" only when the health line is all ticks and no note or suggestion is open.
    // The notes the screen lists: the two-panel Review leaves out the set-at-50 note and the pitch line,
    // so "1 note below" never points at nothing (Jovi, 20 Oct).
    const notesOpen = useMemo(
        () =>
            draft
                ? openDecisions(draft, ["info"]).filter((d) => !((first || draft.kind === "edit_set") && (d.type === "set_narrower" || d.type === "presale_set"))).length
                : 0,
        [draft, first],
    );
    const allClear = useMemo(
        () => (draft ? !healthHasUnmet(draft) && notesOpen === 0 && openDecisions(draft, ["suggestion"]).length === 0 : false),
        [draft, notesOpen],
    );
    const severity = useMemo(() => {
        const map = new Map<number, "blocker" | "decide">();
        // The cards on screen: a re-ask leaves out the trend card, so its rows carry no dot.
        (reaskQs && listDraft ? openGates(listDraft) : gates).forEach((d) =>
            d.slots.forEach((n) => {
                if (d.severity === "blocker" || !map.has(n)) map.set(n, d.severity);
            }),
        );
        return map;
    }, [gates, reaskQs, listDraft]);

    // First AI check: two panels, decide on the left and the 50 on the right (Jovi, 20 Oct).
    // An edit of the questions (Edit and rescan) is the same review: same two panels (Jovi, 20 Oct).
    const split = first || draft?.kind === "edit_set";
    const reask = useReask(draft, reaskQs ?? []);
    // An edit of a finished scan: how that scan's questions were corrected (Edited · V2), read again
    // once a re-ask lands. Staff only, like every edit (Jovi, 20 Oct).
    const sourceId = draft?.kind === "edit_set" ? draft.sourceAssessmentId : null;
    const [scanEdits, setScanEdits] = useState<Record<number, AeoQuestionCorrected>>({});
    const reaskDone = reask.phase === "done";
    useEffect(() => {
        // Cleared first: a failed read must not leave another scan's history on these rows (Jack, #4468).
        setScanEdits({});
        if (!sourceId) return;
        let live = true;
        getAssessment(sourceId)
            .then((a) => {
                if (!live) return;
                const map: Record<number, AeoQuestionCorrected> = {};
                const queries = (a.results as { queries?: AeoQuery[] } | null | undefined)?.queries ?? [];
                queries.forEach((q, i) => {
                    if (q.corrected) map[q.n ?? i + 1] = q.corrected;
                });
                setScanEdits(map);
            })
            .catch(() => undefined);
        return () => {
            live = false;
        };
    }, [sourceId, reaskDone]);
    const editOf = useCallback(
        (n: number): QuestionEdit | null => {
            const run = reask.rows.find((r) => r.q.n === n);
            const c = scanEdits[n];
            // In an edit, a question staff reworded reads "Edited", never "Yours", even before it is re-asked (Jovi, 20 Oct).
            const q = draft?.questions.find((x) => x.n === n);
            const pending = !!q && (q.reworded || q.swapped);
            if (!c && !pending && run?.state !== "asking" && run?.state !== "asked") return null;
            const day = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
            const history = c?.history?.length
                ? [...c.history].reverse().map((h) => `V${h.version}, ${day(h.at)}${h.by ? ` by ${h.by}` : ""}. Was: "${h.was}"`).join("\n")
                : c
                  ? `Edited ${day(c.at)}${c.by ? ` by ${c.by}` : ""}${c.was ? `. Was: "${c.was}"` : ""}`
                  : run?.state === "asked"
                    ? "Edited just now"
                    : "Reworded in this edit. Not re-asked yet.";
            return { version: c?.version ?? null, history, reasking: run?.state === "asking" };
        },
        [draft, reask.rows, scanEdits],
    );
    const passes = useCallback(
        (f: RowFilter, q: ScanDraftQuestion) =>
            f.kind === "all"
                ? true
                : f.kind === "card"
                  ? f.ns.includes(q.n)
                  : f.kind === "edited"
                    ? q.reworded || Boolean(q.swapped)
                    : f.kind === "locked"
                      ? q.locked
                      : f.kind === "changed"
                        ? q.changed
                        : severity.get(q.n) === f.kind,
        [severity],
    );
    const filterCounts = useMemo(() => {
        const rows = draft?.questions ?? [];
        const count = (kind: "blocker" | "decide" | "edited" | "locked" | "changed") => rows.filter((q) => passes({ kind }, q)).length;
        return { blocker: count("blocker"), decide: count("decide"), edited: count("edited"), locked: count("locked"), changed: count("changed") };
    }, [draft, passes]);
    const shownGroups = useMemo(() => {
        if (!split || rowFilter.kind === "all") return groups;
        return groups.map((g) => ({ ...g, rows: g.rows.filter((q) => passes(rowFilter, q)) })).filter((g) => g.rows.length > 0);
    }, [split, rowFilter, groups, passes]);
    // A filter that empties (the last one fixed) or a card that closed goes back to all of them.
    useEffect(() => {
        if (rowFilter.kind === "all") return;
        if (rowFilter.kind === "card") {
            const open = rowFilter.key.startsWith("place:")
                ? placeGroups.some((g) => `place:${g.place}` === rowFilter.key)
                : Boolean(draft?.decisions.some((d) => d.id === rowFilter.key && d.state === "open"));
            if (!open) setRowFilter(ALL_ROWS);
            return;
        }
        if (filterCounts[rowFilter.kind] === 0) setRowFilter(ALL_ROWS);
    }, [rowFilter, placeGroups, draft, filterCounts]);
    const showRows = useCallback(
        (key: string, label: string, ns: number[]) => {
            setView("questions");
            setRowFilter({ kind: "card", key, label, ns });
            setSaid(COPY.forCard(ns.length, label));
            const keep = new Set(ns);
            allGroups.filter((g) => g.rows.some((q) => keep.has(q.n))).forEach((g) => openGroup(g.id, { persist: false }));
        },
        [allGroups, openGroup],
    );

    const status = draft?.status ?? null;
    const building = status === "building" || status === "failed";
    const failed = status === "failed" || draft?.job?.status === "failed";
    // Only a ready draft takes edits; approved and started ones are read here, not changed.
    const readOnly = status !== "ready";
    const total = draft?.counts.total || draft?.questions.length || 50;
    const recipeOpen = Boolean(
        draft?.kind === "recipe_change" && draft.recipeChange?.mode === null && gates.some((d) => d.type === "recipe_change"),
    );
    const choosing = Boolean(chooseFoot);

    /* ---- moving the screen for a decision ---- */
    const openEditor = useCallback(
        (n: number) => {
            setView("questions");
            setOpenN(n);
            const row = draft?.questions.find((q) => q.n === n);
            setRowFilter((f) => (row && passes(f, row) ? f : ALL_ROWS));
            // A row the search filter hides: show all of them again.
            if (!draft?.questions.find((q) => q.n === n)?.search) setSearchOnly(false);
            if (!changedNs.includes(n)) setChangesOnly(false);
            const g = allGroups.find((x) => x.rows.some((q) => q.n === n));
            if (g) openGroup(g.id, { persist: false });
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    document
                        .querySelector<HTMLElement>(`[data-q-row="${n}"]`)
                        ?.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
                }),
            );
        },
        [allGroups, openGroup, reduced, draft, changedNs, passes],
    );
    const openWord = useCallback(
        (variable: string) => {
            setOpenN(null);
            setView(brand ? "searches" : "words");
            setFocusWord(variable);
        },
        [brand],
    );
    /** Searches: "Used in 7" brings the question into view in Questions, editor closed, focus on its wording. */
    const showQuestion = useCallback(
        (n: number) => {
            setView("questions");
            setOpenN(null);
            if (!draft?.questions.find((q) => q.n === n)?.search) setSearchOnly(false);
            if (!changedNs.includes(n)) setChangesOnly(false);
            const g = allGroups.find((x) => x.rows.some((q) => q.n === n));
            if (g) openGroup(g.id, { persist: false });
            requestAnimationFrame(() =>
                requestAnimationFrame(() => {
                    const row = document.querySelector<HTMLElement>(`[data-q-row="${n}"]`);
                    row?.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
                    document.querySelector<HTMLElement>(`[data-q-title="${n}"]`)?.focus({ preventScroll: true });
                }),
            );
        },
        [allGroups, openGroup, reduced, draft, changedNs],
    );
    const business = draft?.business;
    // "Name the area", "Add an outlet", "Choose the 9": the outlets are on the Business step.
    const openBusiness = useCallback(
        (area?: string) => {
            // "Name the area": the outlet field opens with the form's area in it, its town worked out.
            // "Add an outlet" (""): the field opens empty and takes the focus (Angel, 15 Oct). "Choose the 9": the list only.
            setOutletIntent(area ? { name: area, town: townForArea(area), source: "form" } : area === "" ? { name: "", town: null, source: "staff" } : null);
            goTo("business");
        },
        [goTo],
    );
    const seeChanges = useCallback(
        (on: boolean) => {
            setChangesOnly(on);
            if (on) {
                setView("questions");
                setSearchOnly(false);
                // Every changed row is in view: open the groups they sit in.
                const keep = new Set(changedNs);
                allGroups.filter((g) => g.rows.some((q) => keep.has(q.n))).forEach((g) => openGroup(g.id, { persist: false }));
            }
            setSaid(on ? COPY.changesOnly(changedNs.length) : COPY.showingAll(total));
        },
        // total is read when the button is pressed; the draft is loaded by then.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [changedNs, allGroups, openGroup],
    );
    const rowMarket = useCallback(
        (q: ScanDraftQuestion) => (brand ? marketName(marketOf(q), business) : null),
        [brand, business],
    );
    const onFocusDone = useCallback(() => setFocusWord(null), []);

    /* ---- the foot ---- */
    const approve = ctl.approve;
    // An approval the server refused (or that never arrived) is said here, not dropped.
    const [approvalFailure, setApprovalFailure] = useState<string | null>(null);
    const busy = ctl.busy;
    const approved = status === "approved" || status === "started";

    /* ---- the draft finishes building: the building panel's title goes, so say it and move focus ---- */
    const [said, setSaid] = useState("");
    const prevStatus = useRef(status);
    useEffect(() => {
        const was = prevStatus.current;
        prevStatus.current = status;
        if (was !== "building" || status !== "ready") return;
        setSaid(COPY.ready(total, left, allClear));
        requestAnimationFrame(() => {
            const a = document.activeElement;
            if (!a || a === document.body) document.querySelector<HTMLElement>("[data-step-title]")?.focus({ preventScroll: true });
        });
    }, [status, total, left, allClear]);
    /* ---- a decision is answered (or a new one appears): the hidden line says the new count ---- */
    const prevLeft = useRef(left);
    useEffect(() => {
        const was = prevLeft.current;
        prevLeft.current = left;
        if (was === left || status !== "ready") return;
        setSaid(COPY.ready(total, left, allClear));
    }, [status, total, left, allClear]);
    // The filter needs the counts: it goes when Search Console does.
    const searchable = Boolean(draft?.search.connected && !draft.search.pulling && draft.search.matched > 0);
    const filtering = searchOnly && searchable;
    const filterSearch = useCallback(
        (on: boolean) => {
            setSearchOnly(on);
            if (on) {
                setView("questions");
                setChangesOnly(false);
            }
            const n = draft?.questions.filter((q) => q.search).length ?? 0;
            setSaid(on ? COPY.bySearch(n) : COPY.showingAll(total));
        },
        [draft, total],
    );
    const stale = Boolean(draft?.stale);
    useEffect(() => {
        const back = canReach("business") ? { onClick: () => goTo("business") } : null;
        if (!draft) {
            foot.set(null);
            return;
        }
        if (chooseFoot) {
            foot.set({ back: { label: FRAME.back, onClick: chooseFoot.cancel }, status: chooseFoot.status, primary: chooseFoot.primary });
            return;
        }
        if (approved) {
            foot.set({ back, primary: { label: COPY.toStart, onClick: () => goTo("start") } });
            return;
        }
        if (building) {
            // The failed panel carries its own Try again: one primary on the screen.
            // The panel says how far along it is: the foot does not repeat it.
            foot.set({ back, status: null });
            return;
        }

        if (reaskQs && split) {
            // Only reworded questions changed: re-ask those, no approve and no full rescan (Reask.tsx).
            const working = reask.phase === "checking" || reask.phase === "running";
            foot.set({
                back,
                status: left > 0 ? COPY.left(left) : null,
                primary:
                    reask.phase === "done"
                        ? undefined
                        : {
                              label: COPY.reask(reaskQs.length),
                              icon: RefreshCwIcon,
                              disabled: left > 0 || formUnavailable || working || (busy !== null && !working),
                              busy: working,
                              onClick: () => reask.setConfirming(true),
                          },
            });
            return;
        }

        foot.set({
            back,
            status: stale ? COPY.stale : left > 0 ? COPY.left(left) : null,
            primary: {
                label: COPY.approve(total),
                disabled: stale || left > 0 || formUnavailable || (busy !== null && busy !== "Approving"),
                busy: busy === "Approving",
                onClick: async () => {
                    setApprovalFailure(null);
                    const fail = await approve();
                    if (fail) {
                        setApprovalFailure(fail.kind === "network" ? FRAME.network : fail.message);
                        return;
                    }
                    goTo("start");
                },
            },
        });
    }, [draft, chooseFoot, approved, building, failed, stale, left, total, busy, approve, goTo, canReach, foot, reaskQs, split, reask, formUnavailable]);

    // Mounted before the draft is ready, so a screen reader hears the change.
    const region = (
        <p aria-live="polite" className="sr-only">
            {said}
        </p>
    );

    if (!draft) {
        return (
            <>
                {region}
                <Surface>
                    <DrawerPulse lines={3} label={FRAME.loading} />
                </Surface>
            </>
        );
    }

    if (building) {
        // A recipe update cannot be drafted again in place: it starts over as a fresh one.
        const fresh = draft.kind === "recipe_change";
        return (
            <>
                {region}
                <DraftBuilding
                    draft={draft}
                    retrying={fresh ? busy === "Discarding" : busy === "Drafting"}
                    onRetry={() => void (fresh ? flow.startOver() : ctl.rebuild())}
                    onCheckBusiness={canReach("business") ? () => goTo("business") : undefined}
                />
            </>
        );
    }

    // The section holds the goal, the branches and every card that is not "For the plan".
    const toDecide = draft.decisions.some((d) => d.state === "open" && !isPlanCard(d) && (d.severity !== "info" || d.type === "form_goal_core"));

    const sentence =
        left > 0 ? (
            <>
                <span className="font-semibold text-gray-900">{COPY.needs(left)}</span> {COPY.rest}
            </>
        ) : allClear ? (
            COPY.clean
        ) : notesOpen > 0 ? (
            COPY.cleanWithNotes(notesOpen)
        ) : (
            COPY.cleanNoNotes
        );

    const tabs = (
        <Seg<View>
            as="tablist"
            ariaLabel={COPY.tabs}
            panelId={PANEL_ID}
            value={view}
            onChange={(v) => {
                setView(v);
                if (v === "questions") setFocusWord(null);
            }}
            items={[
                { key: "questions", label: COPY.questions, count: draft.questions.length },
                brand
                    ? { key: "searches", label: COPY.searches, count: draft.demand?.suggestions ?? 0 }
                    : { key: "words", label: COPY.words, count: draft.words.length },
            ]}
        />
    );
    const panel = (
        <div role="tabpanel" id={PANEL_ID} aria-labelledby={`${PANEL_ID}-tab-${view}`}>
            {view === "questions" ? (
                <QuestionList
                    groups={split ? shownGroups : groups}
                    words={words}
                    severity={severity}
                    openN={openN}
                    onOpen={setOpenN}
                    onOpenWord={openWord}
                    readOnly={readOnly}
                    searchOnly={filtering}
                    onShowAll={() => filterSearch(false)}
                    // The chip only tells rows apart when the brand sells to more than 1 market.
                    marketOf={brand && (draft.business.markets?.length ?? 0) > 1 ? rowMarket : undefined}
                    editOf={sourceId ? editOf : undefined}
                />
            ) : view === "searches" ? (
                <SearchesView onOpenQuestion={openEditor} onShowQuestion={showQuestion} focusVariable={focusWord} onFocusDone={onFocusDone} />
            ) : (
                <WordsView focusVariable={focusWord} onFocusDone={onFocusDone} />
            )}
        </div>
    );

    if (split) {
        const base = listDraft ?? draft;
        const listed = new Set<DecisionType>(DECISION_SECTIONS.flatMap((x) => x.types));
        const inSection = (d: (typeof base.decisions)[number]) => d.severity !== "info" && d.severity !== "suggestion" && !OWN_PLACE.includes(d.type);
        const sections = [
            ...DECISION_SECTIONS,
            { id: "other", title: COPY.otherChecks, types: [] as DecisionType[] },
        ]
            .map((sec) => {
                const decisions = base.decisions.filter((d) => inSection(d) && (sec.id === "other" ? !listed.has(d.type) : sec.types.includes(d.type)));
                return { ...sec, draft: { ...base, decisions }, open: decisions.filter((d) => d.state === "open").length };
            })
            // A section shows while it has an open card, and stays (marked Done, its outcome line holding
            // Undo) once it had one on this visit; a card resolved before staff came never shows.
            .filter((sec) => {
                if (sec.open > 0) seenSections.current.add(sec.id);
                return seenSections.current.has(sec.id);
            });
        // "1 extra questions keep the set at 50" (set_narrower as a note) changes nothing staff can act on: not shown (Jovi, 20 Oct).
        // A short set (fewer than 50) still comes as a decision.
        const notes = base.decisions.filter((d) => d.severity === "info" && d.type !== "presale_set" && d.type !== "set_narrower");
        const notesDraft = notes.length ? { ...base, decisions: notes } : null;
        // Core service shows whenever the 50 have one, not only when a goal raised a card, and with no
        // core yet whenever the picker has a service to offer: the same lists the picker reads (Jack, #4501).
        const hasGoal = showsCoreService(draft);
        const pills: { f: RowFilter; label: string; count: number; dot?: string; tip?: string }[] = [
            { f: ALL_ROWS, label: COPY.filterAll(draft.questions.length), count: -1 },
            { f: { kind: "blocker" }, label: COPY.filterBlocker, count: filterCounts.blocker, dot: "bg-rose-500", tip: COPY.filterBlockerTip },
            { f: { kind: "decide" }, label: COPY.filterDecide, count: filterCounts.decide, dot: "bg-amber-500", tip: COPY.filterDecideTip },
            { f: { kind: "edited" }, label: COPY.filterEdited, count: filterCounts.edited },
            // An edit: the questions that differ from the set that ran.
            ...(draft.kind === "edit_set" ? [{ f: { kind: "changed" } as RowFilter, label: COPY.filterChanged, count: filterCounts.changed }] : []),
            { f: { kind: "locked" }, label: COPY.filterLocked, count: filterCounts.locked },
        ];
        const filterBar = (
            <>
                {view === "questions" && pills.filter((x) => x.count > 0).length > 0 && (
                    <div role="group" aria-label={COPY.filters} className="flex flex-wrap items-center gap-1">
                        {pills
                            .filter((x) => x.count !== 0)
                            .map((x) => {
                                const on = rowFilter.kind === x.f.kind;
                                return (
                                    <button
                                        key={x.f.kind}
                                        type="button"
                                        aria-pressed={on}
                                        title={x.tip}
                                        onClick={() => setRowFilter(on && x.f.kind !== "all" ? ALL_ROWS : x.f)}
                                        className={cn(
                                            "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium motion-safe:transition-colors",
                                            on ? "bg-blue-600 text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                                            FOCUS_RING,
                                        )}
                                    >
                                        {x.dot && <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", x.dot)} />}
                                        {x.label}
                                        {x.count > 0 && <span className={cn("tabular-nums", on ? "text-blue-100" : "text-gray-400")}>{x.count}</span>}
                                    </button>
                                );
                            })}
                    </div>
                )}
                {view === "questions" && rowFilter.kind === "card" && (
                    <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600")}>
                        {COPY.forCard(rowFilter.ns.length, rowFilter.label)}
                        <PillButton variant="text" onClick={() => setRowFilter(ALL_ROWS)}>
                            {COPY.showAll(total)}
                        </PillButton>
                    </p>
                )}
                {showingChanges && view === "questions" && (
                    <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600")}>
                        {COPY.changesOnly(changedNs.length)}
                        <PillButton variant="text" onClick={() => seeChanges(false)}>
                            {COPY.showAll(total)}
                        </PillButton>
                    </p>
                )}
                {/* The shape and "every outlet asked" only matter when one is off; while the branches are open
                    both only said "waiting on the branches" (Jovi, 20 Oct). */}
                {branches && view === "questions" && openBranchCards(draft).length === 0 && healthHasUnmet(draft) && (
                    <HealthLine draft={draft} searchOnly={filtering} onSearchOnly={filterSearch} branchesOpen={false} />
                )}
                {/* An edit has no branches card: its health line (and Search Console) shows as on the old layout. */}
                {!branches && view === "questions" && <HealthLine draft={draft} searchOnly={filtering} onSearchOnly={filterSearch} />}
            </>
        );
        return (
            <>
                {region}
                {/* Two panels that each scroll from lg up (the page's bars stay put): the 50 on the left, where
                    the eye lands and the width is, and everything staff act on to the right (Jovi, 20 Oct). */}
                <div className="flex flex-col gap-10 lg:grid lg:h-full lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-0">
                    <section aria-label={COPY.theQuestions(total)} className="order-2 min-w-0 space-y-3 lg:order-1 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:py-6 lg:pr-8">
                        <StepHead title={COPY.title(total)} sentence={sentence} right={tabs} className="mb-3" />
                        {reaskQs && (
                            <>
                                <ReaskProgress
                                    key={draft.sourceAssessmentId ?? "none"}
                                    reask={reask}
                                    assessmentId={draft.sourceAssessmentId}
                                    onBack={async () => {
                                        // The corrections are on the scan itself: the edit draft has nothing left to start.
                                        await ctl.discard();
                                        flow.close();
                                    }}
                                />
                                <ReaskConfirm reask={reask} count={reaskQs.length} total={total} />
                            </>
                        )}
                        {filterBar}
                        {panel}
                    </section>

                    <aside
                        aria-label={COPY.toDecide}
                        className="order-1 min-w-0 space-y-5 lg:order-2 lg:-mr-8 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain lg:border-l lg:border-gray-200 lg:bg-white/60 lg:px-8 lg:py-6"
                    >
                        <PitchLine draft={draft} showing={showingChanges} onSeeChanges={() => seeChanges(true)} />
                        {/* The client's goals and plans first: every answer below reads against them (Jovi, 20 Oct). */}
                        <FormContext draft={draft} readOnly={readOnly} withGoal={!branches} fallback={formAnswers} />
                        {formLoadFailed && (
                            <DrawerNotice
                                action={
                                    <PillButton size="sm" onClick={() => setFormLoadAttempt((n) => n + 1)}>
                                        {COPY.formRetry}
                                    </PillButton>
                                }
                            >
                                {COPY.formFailed}
                            </DrawerNotice>
                        )}

                        {stale && (
                            <DrawerNotice
                                action={
                                    <PillButton size="sm" busy={busy === "Drafting"} disabled={busy !== null && busy !== "Drafting"} onClick={() => void ctl.rebuild()}>
                                        {COPY.draftAgain}
                                    </PillButton>
                                }
                            >
                                {COPY.staleNotice}
                            </DrawerNotice>
                        )}
                        {approvalFailure && <Refusal>{approvalFailure}</Refusal>}
                        {status === "started" && <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{COPY.started}</p>}
                        {status === "approved" && (
                            <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600")}>
                                {carriedText(draft) ? COPY.carried(carriedText(draft) ?? "") : COPY.approved}
                                <PillButton variant="text" busy={busy === "Reopening"} disabled={busy !== null && busy !== "Reopening"} onClick={() => void ctl.reopen()}>
                                    {COPY.reopen}
                                </PillButton>
                            </p>
                        )}

                        {hasGoal && (
                            <AsideSection title={COPY.coreService}>
                                <GoalCard draft={draft} readOnly={readOnly} onChangeWord={openWord} />
                            </AsideSection>
                        )}
                        {branches && (
                            <AsideSection title={branchesTitle(draft.business.businessName)}>
                                <BranchesCard key={draft.id} draft={draft} readOnly={readOnly} applier={applier} left={left} headless />
                            </AsideSection>
                        )}
                        {placeGroups.length > 0 && (
                            <AsideSection title={COPY.nameAsPlace}>
                                {placeGroups.map((g) => (
                                    <PlaceNameCard
                                        key={g.place}
                                        group={g}
                                        readOnly={readOnly}
                                        onOpenEditor={openEditor}
                                        onShowRows={() => showRows(`place:${g.place}`, g.place, g.slots)}
                                        showing={rowFilter.kind === "card" && rowFilter.key === `place:${g.place}`}
                                    />
                                ))}
                            </AsideSection>
                        )}
                        {sections.map((sec) => (
                            <AsideSection key={sec.id} title={sec.title} done={sec.open === 0}>
                                <DecisionList
                                    draft={sec.draft}
                                    hideOutcome={branches && (applier.running || applier.canUndo)}
                                    readOnly={readOnly}
                                    onOpenEditor={openEditor}
                                    onOpenWord={openWord}
                                    onOpenSearches={brand ? () => setView("searches") : undefined}
                                    onOpenBusiness={openBusiness}
                                />
                            </AsideSection>
                        ))}
                        {notesDraft && (
                            <DecisionList draft={notesDraft} readOnly={readOnly} onOpenEditor={openEditor} onOpenWord={openWord} onOpenBusiness={openBusiness} />
                        )}

                        <PlanDecisions draft={listDraft ?? draft} readOnly={readOnly} />
                        {(draft.firstCheck?.customerQuestions?.length ?? 0) > 0 && (
                            <AsideSection title={COPY.customerQs} sub={COPY.customerQsSub}>
                                <CustomerQuestions draft={draft} readOnly={readOnly} inline />
                            </AsideSection>
                        )}
                    </aside>
                </div>
                {branches && !readOnly && <TellOuraDock key={draft.id} draft={draft} applier={applier} left={left} />}
            </>
        );
    }

    return (
        <>
            {region}
            <div>
                <StepHead
                    title={COPY.title(total)}
                    sentence={sentence}
                    right={
                        // While choose-each is open the panel is hidden, so the tabs go too.
                        choosing ? undefined : (
                            <Seg<View>
                                as="tablist"
                                ariaLabel={COPY.tabs}
                                panelId={PANEL_ID}
                                value={view}
                                onChange={(v) => {
                                    setView(v);
                                    if (v === "questions") setFocusWord(null);
                                }}
                                items={[
                                    { key: "questions", label: COPY.questions, count: draft.questions.length },
                                    brand
                                        ? { key: "searches", label: COPY.searches, count: draft.demand?.suggestions ?? 0 }
                                        : { key: "words", label: COPY.words, count: draft.words.length },
                                ]}
                            />
                        )
                    }
                />

                <div className="space-y-5">
                    {first && <PitchLine draft={draft} showing={showingChanges} onSeeChanges={() => seeChanges(true)} />}

                    {/* The client's own words for staff: goals, plans, anything else (H3-b). Read-only. */}
                    {first && !choosing && <FormContext draft={draft} readOnly={readOnly} withGoal={!branches} />}

                    {!branches && <HealthLine draft={draft} searchOnly={filtering && view === "questions"} onSearchOnly={filterSearch} />}

                    {stale && (
                        <DrawerNotice
                            action={
                                <PillButton
                                    size="sm"
                                    busy={busy === "Drafting"}
                                    disabled={busy !== null && busy !== "Drafting"}
                                    onClick={() => void ctl.rebuild()}
                                >
                                    {COPY.draftAgain}
                                </PillButton>
                            }
                        >
                            {COPY.staleNotice}
                        </DrawerNotice>
                    )}

                    {approvalFailure && <Refusal>{approvalFailure}</Refusal>}

                    {status === "started" && <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{COPY.started}</p>}

                    {status === "approved" && (
                        <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600")}>
                            {first && carriedText(draft) ? COPY.carried(carriedText(draft) ?? "") : COPY.approved}
                            <PillButton
                                variant="text"
                                busy={busy === "Reopening"}
                                disabled={busy !== null && busy !== "Reopening"}
                                onClick={() => void ctl.reopen()}
                            >
                                {COPY.reopen}
                            </PillButton>
                        </p>
                    )}

                    {branches && !choosing && toDecide && (
                        <SectionHead title={COPY.toDecide} count={left || undefined} sub={readOnly ? undefined : COPY.toDecideSub} />
                    )}
                    {branches && !readOnly && <TellOura key={draft.id} draft={draft} applier={applier} left={left} />}
                    {branches && !choosing && <GoalCard draft={draft} readOnly={readOnly} />}
                    {branches && <BranchesCard key={draft.id} draft={draft} readOnly={readOnly} applier={applier} left={left} />}
                    {placeGroups.map((g) => (
                        <PlaceNameCard key={g.place} group={g} readOnly={readOnly} onOpenEditor={openEditor} />
                    ))}

                    <DecisionList
                        draft={listDraft ?? draft}
                        hideOutcome={branches && (applier.running || applier.canUndo)}
                        recipeCard={recipeOpen && !readOnly ? <RecipeChangeCard onChoosing={setChooseFoot} /> : undefined}
                        readOnly={readOnly}
                        onOpenEditor={openEditor}
                        onOpenWord={openWord}
                        onOpenSearches={brand ? () => setView("searches") : undefined}
                        onOpenBusiness={first ? openBusiness : undefined}
                    />

                    {/* Optional, never a decision: customer questions staff may swap in by hand. */}
                    {first && !choosing && <CustomerQuestions draft={draft} readOnly={readOnly} />}

                    {first && !choosing && <PlanDecisions draft={listDraft ?? draft} readOnly={readOnly} />}

                    {showingChanges && !choosing && view === "questions" && (
                        <p className={cn(TYPE.tc, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-600")}>
                            {COPY.changesOnly(changedNs.length)}
                            <PillButton variant="text" onClick={() => seeChanges(false)}>
                                {COPY.showAll(total)}
                            </PillButton>
                        </p>
                    )}

                    {branches && !choosing && (
                        <div className="space-y-2.5 pt-3">
                            <SectionHead title={COPY.theQuestions(total)} />
                            <HealthLine
                                draft={draft}
                                searchOnly={filtering && view === "questions"}
                                onSearchOnly={filterSearch}
                                branchesOpen={openBranchCards(draft).length > 0}
                            />
                        </div>
                    )}

                    {!choosing && (
                        <div role="tabpanel" id={PANEL_ID} aria-labelledby={`${PANEL_ID}-tab-${view}`}>
                            {view === "questions" ? (
                                <QuestionList
                                    groups={groups}
                                    words={words}
                                    severity={severity}
                                    openN={openN}
                                    onOpen={setOpenN}
                                    onOpenWord={openWord}
                                    readOnly={readOnly}
                                    searchOnly={filtering}
                                    onShowAll={() => filterSearch(false)}
                                    // The chip only tells rows apart when the brand sells to more than 1 market.
                                    marketOf={brand && (draft.business.markets?.length ?? 0) > 1 ? rowMarket : undefined}
                                />
                            ) : view === "searches" ? (
                                <SearchesView
                                    onOpenQuestion={openEditor}
                                    onShowQuestion={showQuestion}
                                    focusVariable={focusWord}
                                    onFocusDone={onFocusDone}
                                />
                            ) : (
                                <WordsView focusVariable={focusWord} onFocusDone={onFocusDone} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

/**
 * The pitch line (JOURNEY-SPEC §2.5 item 1): one ink2 line under the head, the
 * server's sentence. It speaks of questions, never of the pitch's number.
 * "See the changes" when some of the 50 differ from the pitch.
 */
function PitchLine({ draft, showing, onSeeChanges }: { draft: ScanDraft; showing: boolean; onSeeChanges: () => void }) {
    const line = pitchLine(draft);
    if (!line) return null;
    const changed = draft.changedVsPitch ?? 0;
    return (
        <p className={cn(TYPE.tb, "m-0 -mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 break-words text-gray-600")}>
            <span className="min-w-0">{line.title}</span>
            {changed > 0 && (draft.diff?.changed.length ?? 0) > 0 && !showing && (
                <PillButton variant="text" onClick={onSeeChanges}>
                    {COPY.seeChanges}
                </PillButton>
            )}
        </p>
    );
}

/** A titled section on the Review's right side: the kind of decision as its heading, and a quiet "Done" once answered. */
function AsideSection({ title, sub, done = false, children }: { title: string; sub?: string; done?: boolean; children: React.ReactNode }) {
    return (
        <section className="space-y-2.5">
            <h2 className={cn(TYPE.tt, "m-0 flex flex-wrap items-baseline gap-x-2 text-gray-900")}>
                {title}
                {done && (
                    <span className={cn(TYPE.tc, "inline-flex items-center gap-1 font-medium text-emerald-700")}>
                        <CheckIcon aria-hidden size={13} strokeWidth={2.5} />
                        {COPY.sectionDone}
                    </span>
                )}
            </h2>
            {sub && <p className={cn(TYPE.tc, "m-0 -mt-1.5 text-gray-500")}>{sub}</p>}
            {children}
        </section>
    );
}
